import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { 
  Users, 
  GraduationCap, 
  Package, 
  Video,
  DollarSign,
  TrendingUp,
  CheckCircle2,
  XCircle,
  Star,
  Mail
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboard() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ratingDetailOpen, setRatingDetailOpen] = useState(false);
  const [ratingDetailStar, setRatingDetailStar] = useState(null);
  const [ratingDetailData, setRatingDetailData] = useState([]);
  const [ratingDetailLoading, setRatingDetailLoading] = useState(false);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const openRatingDetail = async (star) => {
    setRatingDetailStar(star);
    setRatingDetailOpen(true);
    setRatingDetailLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/ratings/${star}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRatingDetailData(response.data.interviews || []);
    } catch (error) {
      console.error('Failed to fetch rating details:', error);
      setRatingDetailData([]);
    } finally {
      setRatingDetailLoading(false);
    }
  };

  const statCards = stats ? [
    {
      title: 'Total Users',
      value: stats.users.total,
      description: `${stats.users.students} students, ${stats.users.agents} agents`,
      icon: Users,
      color: 'text-blue-500',
      bgColor: 'bg-blue-500/10'
    },
    {
      title: 'Universities',
      value: stats.content.universities,
      description: `${stats.content.questions} questions in bank`,
      icon: GraduationCap,
      color: 'text-lime-500',
      bgColor: 'bg-lime-500/10'
    },
    {
      title: 'Active Packages',
      value: stats.content.packages,
      description: 'Available for purchase',
      icon: Package,
      color: 'text-purple-500',
      bgColor: 'bg-purple-500/10'
    },
    {
      title: 'Total Interviews',
      value: stats.interviews.total,
      description: `${stats.interviews.completed} completed`,
      icon: Video,
      color: 'text-orange-500',
      bgColor: 'bg-orange-500/10'
    },
    {
      title: 'Pass Rate',
      value: `${stats.interviews.pass_rate}%`,
      description: `${stats.interviews.passed} passed, ${stats.interviews.failed} failed`,
      icon: TrendingUp,
      color: 'text-green-500',
      bgColor: 'bg-green-500/10'
    },
    {
      title: 'Total Revenue',
      value: `$${stats.revenue.total.toLocaleString()}`,
      description: `${stats.revenue.credits_sold} credits sold`,
      icon: DollarSign,
      color: 'text-emerald-500',
      bgColor: 'bg-emerald-500/10'
    }
  ] : [];

  const quickActions = [
    { label: 'Manage Users', path: '/admin/users', icon: Users },
    { label: 'Add University', path: '/admin/universities', icon: GraduationCap },
    { label: 'Create Package', path: '/admin/packages', icon: Package },
    { label: 'Add Questions', path: '/admin/questions', icon: Video },
    { label: 'New Application', path: '/admin/applications', icon: Video },
    { label: 'View Interviews', path: '/admin/interviews', icon: Video },
  ];

  const starColors = ['', 'text-red-500', 'text-orange-500', 'text-yellow-500', 'text-lime-500', 'text-green-500'];
  const starBgColors = ['', 'bg-red-500', 'bg-orange-500', 'bg-yellow-500', 'bg-lime-500', 'bg-green-500'];

  return (
    <DashboardLayout role="super_admin">
      <div className="space-y-6">
        {/* Welcome */}
        <div>
          <h2 className="font-heading font-bold text-2xl md:text-3xl">Welcome back, Admin</h2>
          <p className="text-muted-foreground">Here's what's happening with your platform today.</p>
        </div>

        {/* Stats Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <Card key={i} className="animate-pulse">
                <CardContent className="pt-6">
                  <div className="h-16 bg-muted rounded"></div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {statCards.map((stat, index) => (
              <Card key={index} data-testid={`stat-card-${index}`}>
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">{stat.title}</p>
                      <p className="text-2xl font-heading font-bold mt-1">{stat.value}</p>
                      <p className="text-xs text-muted-foreground mt-1">{stat.description}</p>
                    </div>
                    <div className={`w-10 h-10 rounded-lg ${stat.bgColor} flex items-center justify-center ${stat.color}`}>
                      <stat.icon className="w-5 h-5" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Quick Actions */}
        <div>
          <h3 className="font-heading font-semibold text-lg mb-4">Quick Actions</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {quickActions.map((action, index) => (
              <Card 
                key={index}
                className="cursor-pointer hover:border-lime-500/50 transition-colors"
                onClick={() => navigate(action.path)}
                data-testid={`quick-action-${index}`}
              >
                <CardContent className="pt-6 text-center">
                  <action.icon className="w-8 h-8 mx-auto text-lime-500 mb-2" />
                  <p className="text-sm font-medium">{action.label}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Bottom Row: Recent Activity + Rating Analytics */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Recent Interviews */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-lg">Recent Interviews</CardTitle>
            </CardHeader>
            <CardContent>
              {stats?.interviews.total === 0 ? (
                <p className="text-muted-foreground text-center py-8">No interviews yet</p>
              ) : (
                <div className="space-y-4">
                  <p className="text-muted-foreground text-center py-4">
                    {stats?.interviews.completed} completed interviews
                  </p>
                  <div className="flex justify-center gap-8">
                    <div className="text-center">
                      <div className="flex items-center gap-2 text-green-500">
                        <CheckCircle2 className="w-5 h-5" />
                        <span className="text-2xl font-bold">{stats?.interviews.passed}</span>
                      </div>
                      <p className="text-xs text-muted-foreground">Passed</p>
                    </div>
                    <div className="text-center">
                      <div className="flex items-center gap-2 text-red-500">
                        <XCircle className="w-5 h-5" />
                        <span className="text-2xl font-bold">{stats?.interviews.failed}</span>
                      </div>
                      <p className="text-xs text-muted-foreground">Failed</p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* Student Rating Analytics */}
          <Card data-testid="rating-analytics-card">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                Student Feedback Ratings
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!stats?.ratings || stats.ratings.total_ratings === 0 ? (
                <p className="text-muted-foreground text-center py-8">No ratings yet</p>
              ) : (
                <div className="space-y-4">
                  {/* Average Rating */}
                  <div className="flex items-center justify-center gap-3">
                    <span className="text-4xl font-bold text-foreground">{stats.ratings.average}</span>
                    <div>
                      <div className="flex gap-0.5">
                        {[1, 2, 3, 4, 5].map(s => (
                          <Star key={s} className={`w-5 h-5 ${s <= Math.round(stats.ratings.average) ? 'text-yellow-500 fill-yellow-500' : 'text-muted-foreground/30'}`} />
                        ))}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">{stats.ratings.total_ratings} total ratings</p>
                    </div>
                  </div>

                  {/* Star Breakdown - clickable bars */}
                  <div className="space-y-2 pt-2">
                    {[5, 4, 3, 2, 1].map(star => {
                      const count = stats.ratings.breakdown[star] || 0;
                      const percentage = stats.ratings.total_ratings > 0 ? (count / stats.ratings.total_ratings) * 100 : 0;
                      return (
                        <button
                          key={star}
                          onClick={() => count > 0 && openRatingDetail(star)}
                          className={`w-full flex items-center gap-3 group ${count > 0 ? 'cursor-pointer hover:bg-muted/50' : 'cursor-default opacity-60'} rounded-lg px-2 py-1.5 transition-colors`}
                          data-testid={`rating-bar-${star}`}
                          disabled={count === 0}
                        >
                          <span className="text-sm font-medium w-8 text-right flex items-center justify-end gap-0.5">
                            {star} <Star className={`w-3 h-3 ${starColors[star]} fill-current`} />
                          </span>
                          <div className="flex-1 h-5 bg-muted rounded-full overflow-hidden">
                            <div
                              className={`h-full ${starBgColors[star]} rounded-full transition-all duration-500`}
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                          <span className="text-sm font-semibold w-10 text-right">{count}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* User Distribution */}
        <div className="grid lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-lg">User Distribution</CardTitle>
            </CardHeader>
            <CardContent>
              {stats?.users.total === 0 ? (
                <p className="text-muted-foreground text-center py-8">No users yet</p>
              ) : (
                <div className="space-y-4">
                  {[
                    { label: 'Students', value: stats?.users.students, color: 'bg-blue-500' },
                    { label: 'Agents', value: stats?.users.agents, color: 'bg-purple-500' },
                    { label: 'Organizations', value: stats?.users.organizations, color: 'bg-orange-500' },
                  ].map((item, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full ${item.color}`}></div>
                      <span className="flex-1 text-sm">{item.label}</span>
                      <span className="font-semibold">{item.value}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Rating Detail Modal */}
      <Dialog open={ratingDetailOpen} onOpenChange={setRatingDetailOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Students who gave {ratingDetailStar}
              <Star className={`w-5 h-5 ${starColors[ratingDetailStar]} fill-current`} />
              <span className="text-muted-foreground font-normal">({ratingDetailData.length} students)</span>
            </DialogTitle>
          </DialogHeader>
          
          {ratingDetailLoading ? (
            <div className="text-center py-8 text-muted-foreground">Loading...</div>
          ) : ratingDetailData.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">No students found</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Interview</TableHead>
                  <TableHead>Student</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>University</TableHead>
                  <TableHead>Result</TableHead>
                  <TableHead>Feedback</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {ratingDetailData.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>
                      <Badge variant="outline" className="font-mono text-xs">{item.interview_number || '-'}</Badge>
                    </TableCell>
                    <TableCell className="font-medium">{item.student_name || '-'}</TableCell>
                    <TableCell>
                      <a href={`mailto:${item.student_email}`} className="text-blue-500 hover:underline text-sm flex items-center gap-1">
                        <Mail className="w-3 h-3" />
                        {item.student_email}
                      </a>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{item.university_name || '-'}</TableCell>
                    <TableCell>
                      {item.overall_result ? (
                        <Badge className={item.overall_result === 'pass' ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500'}>
                          {item.overall_result.toUpperCase()}
                        </Badge>
                      ) : '-'}
                    </TableCell>
                    <TableCell className="max-w-[200px] text-sm text-muted-foreground truncate">
                      {item.student_feedback?.feedback_text || '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
